import { useState, useMemo } from "react";
import { Search, Receipt, Clock, CheckCircle2, XCircle, Building2, Plus, Pencil, Trash2 } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { StatusBadge, RiskBadge, fmtUSD } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Loading from "@/components/dashboard/Loading";
import { useDashboard, useDashboardActions } from "@/hooks/useDashboard";
import { toast } from "sonner";

const statusOptions = ["All", "Approved", "Pending Approval", "Rejected"];
const TXN_STATUSES = ["Approved", "Pending Approval", "Rejected"];
const RISKS = ["Low", "Medium", "High"];
const EMPTY = { agent: "", merchant: "", amount: "", category: "Software", status: "Approved", risk: "Low" };

export default function Transactions() {
  const { data, isLoading } = useDashboard();
  const { setTxnStatus, createTransaction, updateTransaction, deleteTransaction } = useDashboardActions();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const rows = data?.transactions || [];

  const filtered = useMemo(() => {
    return rows.filter((t) => {
      const matchQ =
        t.agent.toLowerCase().includes(query.toLowerCase()) ||
        t.merchant.toLowerCase().includes(query.toLowerCase());
      const matchF = filter === "All" || t.status === filter;
      return matchQ && matchF;
    });
  }, [rows, query, filter]);

  if (isLoading || !data) {
    return (<div><PageHeader title="Transactions" subtitle="Live financial ledger across your agent fleet." /><Loading /></div>);
  }

  const decide = async (id, approve) => {
    await setTxnStatus(id, approve ? "Approved" : "Rejected");
    setActive(null);
    toast.success(`Transaction ${approve ? "approved" : "rejected"}`);
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ agent: t.agent, merchant: t.merchant, amount: t.amount, category: t.category, status: t.status, risk: t.risk });
    setActive(null);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.agent.trim() || !form.merchant.trim()) { toast.error("Agent and merchant are required"); return; }
    setSaving(true);
    const body = {
      agent: form.agent.trim(),
      merchant: form.merchant.trim(),
      amount: Number(form.amount) || 0,
      category: form.category.trim() || "Software",
      status: form.status,
      risk: form.risk,
    };
    try {
      if (editing) { await updateTransaction(editing.id, body); toast.success("Transaction updated"); }
      else { await createTransaction(body); toast.success("Transaction recorded"); }
      setDialogOpen(false);
    } catch { toast.error("Could not save transaction"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await deleteTransaction(toDelete.id); toast.success("Transaction deleted"); }
    catch { toast.error("Could not delete transaction"); }
    finally { setToDelete(null); setActive(null); }
  };

  return (
    <div>
      <PageHeader title="Transactions" subtitle="Live financial ledger across your agent fleet.">
        <Button onClick={openCreate} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full gap-1.5" data-testid="add-transaction-btn">
          <Plus className="h-4 w-4" /> New transaction
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search agent or merchant…"
            className="pl-9 bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]" data-testid="transactions-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-52 bg-white/[0.03] border-white/12 text-white focus:ring-[#4F8CFF]" data-testid="transactions-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0B1526] border-white/10 text-white">
            {statusOptions.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/5">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Panel className="hidden md:block overflow-hidden" data-testid="transactions-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/8 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3.5 font-medium">Date</th>
              <th className="px-5 py-3.5 font-medium">Agent</th>
              <th className="px-5 py-3.5 font-medium">Merchant</th>
              <th className="px-5 py-3.5 font-medium">Category</th>
              <th className="px-5 py-3.5 font-medium text-right">Amount</th>
              <th className="px-5 py-3.5 font-medium">Risk</th>
              <th className="px-5 py-3.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} onClick={() => setActive(t)} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors" data-testid={`txn-row-${t.id}`}>
                <td className="px-5 py-4 text-slate-400 whitespace-nowrap">{t.date}</td>
                <td className="px-5 py-4 text-white">{t.agent}</td>
                <td className="px-5 py-4 text-slate-300">{t.merchant}</td>
                <td className="px-5 py-4 text-slate-400">{t.category}</td>
                <td className="px-5 py-4 text-right text-white font-medium whitespace-nowrap">{fmtUSD(t.amount)}</td>
                <td className="px-5 py-4"><RiskBadge risk={t.risk} /></td>
                <td className="px-5 py-4"><StatusBadge status={t.status} /></td>
              </tr>
            ))}
            {filtered.length === 0 && (<tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500">No transactions match your filters.</td></tr>)}
          </tbody>
        </table>
      </Panel>

      <div className="md:hidden space-y-3">
        {filtered.map((t) => (
          <Panel key={t.id} className="p-4" onClick={() => setActive(t)} data-testid={`txn-card-${t.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white">{t.agent}</span>
              <span className="text-sm font-medium text-white">{fmtUSD(t.amount)}</span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{t.merchant} · {t.category}</p>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-slate-500">{t.date}</span>
              <StatusBadge status={t.status} />
            </div>
          </Panel>
        ))}
        {filtered.length === 0 && <p className="text-center text-slate-500 py-10">No transactions match your filters.</p>}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0B1526] border-white/10 text-white max-w-lg">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-[#4F8CFF]" /> Transaction detail</DialogTitle>
              </DialogHeader>
              <div className="mt-2">
                <p className="text-3xl font-semibold">{fmtUSD(active.amount)}</p>
                <div className="mt-2 flex items-center gap-2"><StatusBadge status={active.status} /><RiskBadge risk={active.risk} /></div>
                <div className="mt-5 grid grid-cols-2 gap-y-3 text-sm">
                  <span className="text-slate-400">Agent</span><span className="text-white text-right">{active.agent}</span>
                  <span className="text-slate-400">Merchant</span><span className="text-white text-right">{active.merchant}</span>
                  <span className="text-slate-400">Category</span><span className="text-white text-right">{active.category}</span>
                  <span className="text-slate-400">Date</span><span className="text-white text-right">{active.date}</span>
                </div>
                {active.status === "Pending Approval" && (
                  <div className="mt-5 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                    <p className="text-xs font-mono uppercase tracking-wider text-amber-400">Reason</p>
                    <p className="mt-1 text-sm text-slate-200">Transaction exceeds the agent's approval threshold.</p>
                  </div>
                )}
                <div className="mt-6">
                  <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Timeline</p>
                  <div className="space-y-4">
                    {[
                      { icon: Building2, label: "Initiated by agent", time: active.date, color: "text-slate-300" },
                      { icon: Clock, label: "Policy evaluated", time: active.approval === "Auto" ? "Auto-approved by policy" : "Flagged for approval", color: "text-[#4F8CFF]" },
                      active.status === "Approved" ? { icon: CheckCircle2, label: "Approved", time: "Payment authorized", color: "text-emerald-400" }
                        : active.status === "Rejected" ? { icon: XCircle, label: "Rejected", time: "Payment blocked", color: "text-red-400" }
                        : { icon: Clock, label: "Awaiting human approval", time: "Pending", color: "text-amber-400" },
                    ].map((s, i) => {
                      const Icon = s.icon;
                      return (
                        <div key={i} className="flex gap-3">
                          <span className={`mt-0.5 ${s.color}`}><Icon className="h-4 w-4" /></span>
                          <div><p className="text-sm text-white">{s.label}</p><p className="text-xs text-slate-500">{s.time}</p></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-6 flex gap-2">
                  <Button onClick={() => openEdit(active)} variant="outline" className="flex-1 bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full gap-1.5" data-testid="txn-edit-btn">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button onClick={() => setToDelete(active)} variant="outline" className="flex-1 bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full gap-1.5" data-testid="txn-delete-btn">
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
                {active.status === "Pending Approval" && (
                  <div className="mt-3 flex gap-3">
                    <Button onClick={() => decide(active.id, true)} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full" data-testid="txn-approve-btn">Approve Transaction</Button>
                    <Button onClick={() => decide(active.id, false)} variant="outline" className="flex-1 bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full" data-testid="txn-reject-btn">Reject Transaction</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A1119] border-white/10 text-white" data-testid="transaction-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit transaction" : "New transaction"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Agent</Label>
                <Input value={form.agent} onChange={(e) => setForm({ ...form, agent: e.target.value })} placeholder="e.g. Research Agent" className="bg-white/[0.03] border-white/12 text-white" data-testid="txn-form-agent" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Merchant</Label>
                <Input value={form.merchant} onChange={(e) => setForm({ ...form, merchant: e.target.value })} placeholder="e.g. OpenAI API" className="bg-white/[0.03] border-white/12 text-white" data-testid="txn-form-merchant" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Amount ($)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-white/[0.03] border-white/12 text-white" data-testid="txn-form-amount" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white/[0.03] border-white/12 text-white" data-testid="txn-form-category" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-white/[0.03] border-white/12 text-white" data-testid="txn-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A1119] border-white/10 text-white">
                    {TXN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Risk</Label>
                <Select value={form.risk} onValueChange={(v) => setForm({ ...form, risk: v })}>
                  <SelectTrigger className="bg-white/[0.03] border-white/12 text-white" data-testid="txn-form-risk"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A1119] border-white/10 text-white">
                    {RISKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid="txn-form-save">{saving ? "Saving…" : editing ? "Save changes" : "Add transaction"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0A1119] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete transaction?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">This permanently removes the {toDelete?.merchant} transaction and cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="txn-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
